import { createFileRoute } from '@tanstack/react-router'
import * as React from "react";
import { API_ADMIN_BASE_URL } from "@/lib/api-client";
import { Separator } from "@/components/ui/separator";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { SidebarTrigger } from "@/components/ui/sidebar";

interface ModelUsage {
  modelId: string;
  name: string;
  provider: string;
  usageCount: number;
  uniqueUsers: number;
}

function ModelAnalyticsPage() {
  const [models, setModels] = React.useState<ModelUsage[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    document.title = "Model Analytics - Admin";
    fetch(`${API_ADMIN_BASE_URL}/api/admin/analytics/model-usage?limit=30`, { credentials: "include" })
      .then((r) => r.json())
      .then((j) => setModels(j.data.models))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const maxUsage = models.length > 0 ? models[0].usageCount : 1;

  return (
    <div className="bg-background h-full overflow-auto">
      <header>
        <div className="border-border flex h-12 shrink-0 items-center gap-2 border-b px-4 md:h-16 md:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbLink href="/dashboard/analytics/models">Analytics</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>Model Usage</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="border-border border-b px-4 py-4 md:px-6 md:py-6">
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Model Usage Analytics</h1>
          <p className="text-muted-foreground text-sm">Top models by usage across the platform</p>
        </div>
      </header>
      <main className="px-4 py-6 md:px-6 space-y-6">
        {loading ? (
          <p className="text-muted-foreground text-center py-12">Loading...</p>
        ) : models.length === 0 ? (
          <p className="text-muted-foreground text-center py-12">No usage data</p>
        ) : (
          <>
            {/* Bar chart visualization */}
            <div className="space-y-2">
              {models.slice(0, 15).map((m) => (
                <div key={m.modelId} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-[200px] truncate">{m.name}</span>
                  <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                    <div className="h-full bg-primary/60 rounded" style={{ width: `${(m.usageCount / maxUsage) * 100}%` }} />
                  </div>
                  <span className="text-xs font-medium w-[60px] text-right">{m.usageCount.toLocaleString()}</span>
                </div>
              ))}
            </div>

            {/* Table */}
            <div className="relative overflow-hidden rounded-lg border">
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>#</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead className="text-right">Usage Count</TableHead>
                      <TableHead className="text-right">Unique Users</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {models.map((m, i) => (
                      <TableRow key={m.modelId}>
                        <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="font-medium">{m.name}</TableCell>
                        <TableCell className="text-muted-foreground">{m.provider}</TableCell>
                        <TableCell className="text-right">{m.usageCount.toLocaleString()}</TableCell>
                        <TableCell className="text-right">{m.uniqueUsers.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export const Route = createFileRoute('/dashboard/analytics/models')({
  component: ModelAnalyticsPage,
})
